import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { GeneratedSite } from "building-types/site/site";
import { degreeToRadian } from "common/utils/math";
import { makeXYPlane } from "doc/measurementUtils";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { faceNew } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongNormal, extrudeFaceAlongPolyLine3 } from "geom/extrude";
import GeomTagGroup from "geom/geomTagGroup";
import { planeRotateAroundNormal } from "geom/plane";
import { polyLine2EdgeNormal, polyLine2Move, polyLine2SetCW } from "geom/polyLine2";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3EvalEdge, polyLine3Length, polyLine3ToPolyLine2IgnoreZ } from "geom/polyLine3";
import { rect2ByTwoPoints } from "geom/rect2";

export function makeFence(ret: GeneratedSite, fenceEdges: PolyLine3[], fenceHeight: number) {
  const lowRailHeight = 12;
  const topRailHeight = fenceHeight;
  const slatWidth = 12;

  const fenceRailProfile = rect2ByTwoPoints(0, 0, 3.5, 1.5);
  const lowFenceRailProfile = rect2ByTwoPoints(0, 0, 1.5, lowRailHeight);
  polyLine2SetCW(lowFenceRailProfile);
  const topFenceRailProfile = polyLine2Move(fenceRailProfile, 0, topRailHeight);

  for (const edge of fenceEdges) {
    polyLine2SetCW(fenceRailProfile);

    const lowFenceRailGeom = extrudeFaceAlongPolyLine3([lowFenceRailProfile], edge, 0, 0, 1);
    ret.fenceGeom.push({
      group: new GeomTagGroup<ExteriorMaterialOverride>().add(lowFenceRailGeom, exteriorMaterialOverrideNew("fence0")),
      pickHandle: { type: "SITE", id: ret.id },
    });

    const topFenceRailGeom = extrudeFaceAlongPolyLine3([topFenceRailProfile], edge, 0, 0, 1);
    ret.fenceGeom.push({
      group: new GeomTagGroup<ExteriorMaterialOverride>().add(topFenceRailGeom, exteriorMaterialOverrideNew("fence0")),
      pickHandle: { type: "SITE", id: ret.id },
    });

    const edgeLen = polyLine3Length(edge);

    const edgePl2 = polyLine3ToPolyLine2IgnoreZ(edge);

    for (let i = slatWidth / 2; i < edgeLen; i += slatWidth) {
      const [x, y, z] = polyLine3EvalEdge(edge, 0, i, false);
      let slatPlane = makeXYPlane(x, y, z + lowRailHeight);

      const [nX, nY] = polyLine2EdgeNormal(edgePl2, 0);

      if (Math.abs(nY) > 1.0 - DIST_EPSILON) {
        slatPlane = planeRotateAroundNormal(slatPlane, degreeToRadian(90));
      }

      if (Math.abs(nX) > 1.0 - DIST_EPSILON) {
        slatPlane = planeRotateAroundNormal(slatPlane, degreeToRadian(0));
      }

      const slatBase = rect2ByTwoPoints(0, -(slatWidth / 2) + 1, 1.5, slatWidth / 2 - 1);
      polyLine2SetCW(slatBase);
      polyLine2Move(slatBase, 2, 0);
      const slatFace = faceNew([slatBase], slatPlane);
      const slatGeom = extrudeFaceAlongNormal(slatFace, fenceHeight - 12);
      ret.fenceGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().add(slatGeom, exteriorMaterialOverrideNew("fence0")),
        pickHandle: { type: "SITE", id: ret.id },
      });
    }
  }
}
